import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, MessageCircle, Clock, Navigation } from "lucide-react";
import { Layout } from "../components/Layout";
import { eur } from "../lib/format";
import * as api from "../api";

export default function RestaurantDetail() {
  const { slug } = useParams();
  const { data: r, isLoading } = useQuery({ queryKey: ["restaurant", slug], queryFn: () => api.publicRestaurant(slug) });
  if (isLoading) return <Layout><div className="text-center py-24 text-slate-400">Caricamento...</div></Layout>;
  if (!r) return <Layout><div className="text-center py-24 text-slate-400">Ristorante non trovato.</div></Layout>;
  const gallery = r.gallery?.length ? r.gallery : (r.cover ? [r.cover] : []);
  return (
    <Layout noPadding>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {r.cover && <div className="h-64 sm:h-80 rounded-2xl overflow-hidden bg-slate-100"><img src={r.cover} alt={r.brand_name} className="h-full w-full object-cover" /></div>}
        <div className="mt-6 flex items-start gap-4 flex-wrap">
          {r.logo && <img src={r.logo} alt="" className="h-16 w-16 rounded-xl object-cover border" />}
          <div className="flex-1">
            <h1 className="font-heading font-semibold text-3xl sm:text-4xl text-slate-900">{r.brand_name}</h1>
            <p className="mt-1 text-slate-500 flex items-center gap-1"><MapPin className="h-4 w-4" /> {r.address ? r.address + ", " : ""}{r.city}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {r.phone && <a href={`tel:${r.phone}`} data-testid="rest-cta-call" className="rounded-full bg-primary text-white text-sm font-semibold px-4 py-2 flex items-center gap-1"><Phone className="h-4 w-4" /> Chiama</a>}
            {r.whatsapp && <a href={`https://wa.me/${r.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="rest-cta-wa" className="rounded-full bg-emerald-500 text-white text-sm font-semibold px-4 py-2 flex items-center gap-1"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
            {r.address && <a href={`https://maps.google.com/?q=${encodeURIComponent(r.address + " " + r.city)}`} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 text-slate-700 text-sm font-semibold px-4 py-2 flex items-center gap-1"><Navigation className="h-4 w-4" /> Indicazioni</a>}
          </div>
        </div>
        {r.description && <p className="mt-5 text-slate-600 whitespace-pre-line">{r.description}</p>}
        {r.hours && <p className="mt-3 text-sm text-slate-500 flex items-center gap-1"><Clock className="h-4 w-4 text-primary" /> {r.hours}</p>}

        {gallery.length > 1 && <div className="mt-6 grid grid-cols-3 gap-2">{gallery.slice(1, 7).map((g, i) => <img key={i} src={g} alt="" className="h-28 w-full object-cover rounded-lg" />)}</div>}

        <div className="mt-8" data-testid="restaurant-menu">
          <h2 className="font-heading font-semibold text-2xl text-slate-900 mb-4">Menu</h2>
          {(r.menu || []).length === 0 ? <p className="text-slate-400">Menu in preparazione.</p> : r.menu.map((c) => (
            <div key={c.id} className="mb-6" data-testid={`menu-section-${c.id}`}>
              <h3 className="font-heading font-semibold text-lg text-primary border-b border-slate-100 pb-1 mb-3">{c.name}</h3>
              <div className="space-y-3">
                {c.dishes.map((d) => (
                  <div key={d.id} className="flex gap-3" data-testid={`public-dish-${d.id}`}>
                    {d.image && <img src={d.image} alt="" className="h-16 w-20 object-cover rounded-lg" />}
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-semibold text-slate-800">{d.name}</span>
                        <span className="font-semibold text-primary whitespace-nowrap">{eur(d.price)}</span>
                      </div>
                      {d.description && <p className="text-sm text-slate-500">{d.description}</p>}
                      <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] font-semibold">
                        {d.vegetarian && <span className="rounded-full bg-emerald-50 text-emerald-600 px-2 py-0.5">Vegetariano</span>}
                        {d.vegan && <span className="rounded-full bg-emerald-50 text-emerald-600 px-2 py-0.5">Vegano</span>}
                        {d.gluten_free && <span className="rounded-full bg-amber-50 text-amber-600 px-2 py-0.5">Senza glutine</span>}
                        {d.allergens?.length > 0 && <span className="rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Allergeni: {d.allergens.join(", ")}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
